import { useState, useCallback, useEffect, useRef } from 'react';
import { PostgrestError } from '@supabase/supabase-js';
import { useNavigate } from 'react-router-dom';
import { Virtuoso } from 'react-virtuoso';

import './doomscroll.css';

// DB API Calls
import { getPosts_Filtered } from './db/getPosts_Filtered';

// Components
import checkGame from './components/checkGame';
import PostLayout from './components/postLayout';
import NavMenu from './components/navMenu';

// Images
import SearchIcon from './images/icons/magnifying-glass-solid-full.svg';
import NavMenuDesktop from './components/navMenuDesktop';

type PostInfo = {
  name: string,
  hash: number,
  shortdescription: string | null,
  subtitle: string | null,
  released: string | null,
  image: string | null,
  icon: string | null,
  itemtypeandtierdisplayname: string | null,
  lorebookname: string | null,
  doctype: string
}

export default function DoomscrollMain() {
    
    const navigate = useNavigate();
    const [posts, setPosts] = useState<PostInfo[]>([]);

    const virtuosoRef = useRef<any>(null);
    const [loading, setLoading] = useState<boolean>(true);

    const [savedIndex] = useState<number>(sessionStorage.getItem("scrollIndex") === null ? 0 : JSON.parse(sessionStorage.getItem("scrollIndex")!));

    const [testHighestLoadedPost, setTestHighestLoadedPost] = useState<number>(0);

    // Filter Values
    const [searchValue, setSearchValue] = useState<string>("");
    const [searchFocus, setSearchFocus] = useState<boolean>(false);
    const [sortFilter, setSortFilter] = useState<string>(sessionStorage.getItem("sortOrder") === null ? "alphabetical-asc" : JSON.parse(sessionStorage.getItem("sortOrder")!));
    const [seasonFilter, setSeasonFilter] = useState<number>(sessionStorage.getItem("seasonOrder") === null ? -1 : Number(JSON.parse(sessionStorage.getItem("seasonOrder")!)));
    const [docFilter, setDocFilter] = useState<string>(sessionStorage.getItem("docFilter") === null ? "All" : JSON.parse(sessionStorage.getItem("docFilter")!));


    const released = [
        "Destiny", "The Dark Below", "House of Wolves", "The Taken King", "Rise of Iron", "Age of Triumph",
        "Destiny 2", "Curse of Osiris", "Warmind",
        "Forsaken", "Season of the Forge", "Season of the Drifter", "Season of Opulence",
        "Shadowkeep", "Season of the Undying", "Season of Dawn", "Season of the Worthy", "Season of Arrivals", 
        "Beyond Light", "Season of the Hunt", "Season of the Chosen", "Season of the Splicer", "Season of the Lost",
        "Witch Queen", "Season of the Risen", "Season of the Haunted", "Season of Plunder", "Season of the Seraph",
        "Lightfall", "Season of Defiance", "Season of the Deep", "Season of the Witch", "Season of the Wish",
        "Final Shape", "Episode Echoes", "Episode Revenant", "Episode Heresy",
        "Edge of Fate", "Renegades", "Monument of Triumph"
    ];

    // --------------------------------- Functions Start ---------------------------------

    async function getAllPosts(start: number, limit: number) {
        try {
            setLoading(true);
            const res: any = await getPosts_Filtered(start, limit + start, sortFilter)
                .then((data) => {
                    return data;
                })
                .catch((error: PostgrestError) => {
                    console.log("Error: " + error);
                });

            setPosts(res);
            setLoading(false);
        }
        catch(e) {
            console.log(e);
        }
    }

    async function getMorePosts(start: number) {
        try {
            const res: any = await getPosts_Filtered(start, 30, sortFilter)
                .then((data) => {
                    return data;
                }).catch((error: PostgrestError) => {
                    console.log("Error: " + error);
                });

            // console.log("More Posts index: " + (posts.length + res.length));
            return res;
        }
        catch(e) {
            console.log(e);
        }
    }

    const handleIndex = (index: number) => {
        if(loading === false) {
            virtuosoRef.current.scrollToIndex({
                index: index,
                align: 'start',
                behavior: 'auto',
            });
        }        
    };
   
    function handleScroll(index: number) {
         if(window.scrollY < 100) {
            setTestHighestLoadedPost(0);
        }  
        else if(testHighestLoadedPost <= index) {
            setTestHighestLoadedPost(index);    
        }
        else {
            setTestHighestLoadedPost(testHighestLoadedPost - 1);
        }
    }

    const loadMore = useCallback(async() => {
        const res: any = await getMorePosts(posts.length);
        if(res.length !== 0) {
            return setTimeout(() => {
                setPosts((posts) => [...posts, ...res])
            }, 1000); 
        }
        else {
            let loader = document.getElementsByClassName("loader-container");
            if(loader.length !== 0) {
                loader[0].classList.add("display-none");
            }
            return setTimeout(() => {}, 1000);
        }
    }, [setPosts, posts]);

    function saveIndex(index: number) {
        sessionStorage.setItem("scrollIndex", JSON.stringify(index));
    }

    // Filter Functions
    function saveSort(sort: string) {
        setSortFilter(sort);
        sessionStorage.setItem("sortOrder_search", JSON.stringify(sort));
    }
    function saveSeason(sort: number) {
        setSeasonFilter(sort);
        sessionStorage.setItem("seasonOrder_search", JSON.stringify(sort));
    }
    function saveDoc(sort: string) {
        setDocFilter(sort);
        sessionStorage.setItem("docFilter_search", JSON.stringify(sort));
    }
    function startSearch(key: string) {
        if(key === "Enter" && searchValue.length !== 0) {
            navigate(`/search?q=${searchValue}`);
        }
    }
    function updateSearchValue(value: string) {
        setSearchValue(value);
    }

    // --------------------------------- Functions End ---------------------------------


    useEffect(() => {
        handleIndex(savedIndex);

        const clearSessionStorage = () => sessionStorage.clear();
        window.addEventListener("beforeunload", clearSessionStorage); // Clear session storage on refresh  

        if(savedIndex !== 0) {
            getAllPosts(0, savedIndex + 30);
        }
        else {
            getAllPosts(0, 30);
        }
    }, []);

    // When sort order is changed
    useEffect(() => {
        getAllPosts(0, 30);
    }, [sortFilter]);


    if(loading) {
        return(
            <div className="window-container">
                <div className="content-container" >
                    <div className="scroll-area loading">
                        <NavMenu
                            title={"Home"}
                            startSearch={startSearch}
                            saveSort={saveSort}
                            saveDoc={saveDoc}
                            saveSeason={saveSeason}
                            updateSearchValue={updateSearchValue}
                            searchValue={searchValue}
                            sortFilter={sortFilter}
                            seasonFilter={seasonFilter}
                            docFilter={docFilter}
                        />
                        <div
                            style={{
                                padding: '2rem',
                                display: 'flex',
                                justifyContent: 'center',
                            }}
                        >
                            <span className="loader"></span>
                        </div>
                    </div>
                </div>
            </div>
        );
    }
    else {
        return(
            <div className="window-container">
                {/* <div className="scroll-stats">
                    <div>Saved Index - Mod = {savedIndex / 30}</div>
                    <div>Post Length = {posts.length}</div>
                    <div>Highest Loaded Post = {testHighestLoadedPost}</div>
                    <div>Current Viewable Index = {testHighestLoadedPost - 1}</div>
                    <div>Window Scroll Y = {window.scrollY}</div>
                </div> */}
                {/* <NavMenuDesktop /> */}
                <div className="content-container" >
                    <NavMenu
                        title={"Home"}
                        startSearch={startSearch}
                        saveSort={saveSort}
                        saveDoc={saveDoc}
                        saveSeason={saveSeason}
                        updateSearchValue={updateSearchValue}
                        searchValue={searchValue}
                        sortFilter={sortFilter}
                        seasonFilter={seasonFilter}
                        docFilter={docFilter}
                    />
                    <div className="scroll-area">
                        {posts !== null && posts !== undefined && posts.length !== 0 && 
                            <Virtuoso
                                ref={virtuosoRef}
                                className="virtouso"
                                useWindowScroll
                                style={{ height: '100%' }}
                                data={posts}
                                totalCount={posts.length}
                                initialTopMostItemIndex={savedIndex}            
                                endReached={loadMore}
                                // overscan={300}
                                increaseViewportBy={300}
                                itemContent={(index, entry) => {

                                    // Quest
                                    if(entry.itemtypeandtierdisplayname !== undefined && entry.itemtypeandtierdisplayname!.includes("Quest")) {
                                        return(
                                            <PostLayout
                                                type={"Quest"} docType={entry.doctype}
                                                hash={entry.hash} lorebookname={undefined}
                                                name={entry.name} description={entry.shortdescription!}
                                                subtitle={entry.subtitle!} released={entry.released!} image={entry.icon!}
                                                save={saveIndex} index={index} handleScroll={handleScroll} goodImage={entry.image === null ? false : true}
                                            />
                                        );
                                    }

                                    // D2 Lore Entry
                                    else if(entry.doctype === "Lore" && entry.lorebookname === null && !checkGame(entry.released)) {
                                        return(
                                            <PostLayout
                                                type={entry.itemtypeandtierdisplayname!} docType={entry.doctype}
                                                hash={entry.hash} lorebookname={undefined}
                                                name={entry.name} description={entry.shortdescription!}
                                                subtitle={entry.subtitle!} released={entry.released!} image={entry.image!}
                                                save={saveIndex} index={index} handleScroll={handleScroll} goodImage={entry.image === null ? false : true}
                                            />
                                        );
                                    }

                                    // D2 Item - with Image
                                    else if(entry.lorebookname === null && !checkGame(entry.released) && entry.image !== null) {
                                        return(
                                            <PostLayout
                                                type={entry.itemtypeandtierdisplayname!} docType={entry.doctype}
                                                hash={entry.hash} lorebookname={undefined}
                                                name={entry.name} description={entry.shortdescription!}
                                                subtitle={entry.subtitle!} released={entry.released!} image={entry.image!}
                                                save={saveIndex} index={index} handleScroll={handleScroll} goodImage={entry.image === null ? false : true}
                                            />
                                        );
                                    }

                                    // D2 Item - Icon
                                    else if(entry.lorebookname === null && !checkGame(entry.released) && entry.image === null) {
                                        return(
                                            <PostLayout
                                                type={entry.itemtypeandtierdisplayname!} docType={entry.doctype}
                                                hash={entry.hash} lorebookname={undefined}
                                                name={entry.name} description={entry.shortdescription!}
                                                subtitle={entry.subtitle!} released={entry.released!} image={entry.icon!}
                                                save={saveIndex} index={index} handleScroll={handleScroll} goodImage={entry.image === null ? false : true}
                                            />
                                        );
                                    }

                                    // D1 Item
                                    else if(entry.itemtypeandtierdisplayname !== undefined && entry.lorebookname === null && checkGame(entry.released) && entry.itemtypeandtierdisplayname !== "Grimoire") {
                                        return(
                                            <PostLayout
                                                type={entry.itemtypeandtierdisplayname!} docType={entry.doctype}
                                                hash={entry.hash} lorebookname={undefined}
                                                name={entry.name} description={entry.shortdescription!}
                                                subtitle={entry.subtitle!} released={entry.released!} image={entry.icon!}
                                                save={saveIndex} index={index} handleScroll={handleScroll} goodImage={entry.image === null ? false : true}
                                            />
                                        );
                                    }

                                    // Lore Book
                                    else if(entry.lorebookname !== null && !checkGame(entry.released)) {
                                        return(
                                            <PostLayout
                                                type={"Lore Book"} docType={entry.doctype}
                                                hash={entry.hash} lorebookname={entry.lorebookname}
                                                name={entry.name} description={entry.shortdescription!}
                                                subtitle={undefined} released={entry.released!} image={entry.image!}
                                                save={saveIndex} index={index} handleScroll={handleScroll} goodImage={entry.image === null ? false : true}
                                            />
                                        );
                                    }
                                    
                                    // Grimoire
                                    else if(entry.itemtypeandtierdisplayname === "Grimoire") {
                                        return(
                                            <PostLayout
                                                type={entry.itemtypeandtierdisplayname} docType={entry.doctype}
                                                hash={entry.hash} lorebookname={undefined}
                                                name={entry.name} description={entry.shortdescription!}
                                                subtitle={entry.subtitle!} released={entry.released!} image={entry.image!}
                                                save={saveIndex} index={index} handleScroll={handleScroll} goodImage={entry.image === null ? false : true}
                                            />
                                        );
                                    }
                                    
                                    // Unknown
                                    else {
                                        return(
                                        <PostLayout
                                            type={"Unknown Entry"} docType={entry.doctype}
                                            hash={entry.hash} lorebookname={undefined}
                                            name={entry.name} description={entry.shortdescription!}
                                            subtitle={entry.subtitle!} released={entry.released!} image={entry.icon!}
                                            save={saveIndex} index={index} handleScroll={handleScroll} goodImage={false}
                                        />
                                        );
                                    }
                                }}
                                components={{ Footer }}
                            />
                        }
                    </div>

                
                </div>
            </div>
        );
    }
  
}



const Footer = () => {
  return (
    <div
      style={{
        padding: '2rem',
        display: 'flex',
        justifyContent: 'center',
      }}
      className="loader-container"
    >
      <span className="loader"></span>
    </div>
  );
}
